import React, { useState, useRef, useEffect } from 'react';
import {
   Box,
   Button,
   Popover,
   PopoverTrigger,
   PopoverContent,
   PopoverBody,
   VStack,
   Text,
   Input,
   Divider,
   useDisclosure,
   useColorMode,
   useColorModeValue,
} from '@chakra-ui/react';
import { ChevronDownIcon, CheckIcon } from '@chakra-ui/icons';
import Litepicker from 'litepicker';
import 'litepicker/dist/css/litepicker.css';
import moment from 'moment';

const PeriodDatePicker = ({ value, onChange, defaultPeriod = 'last7Days', style, ...props }) => {
   const { colorMode } = useColorMode();

   // Helper function to get period label by key (moved up to be available during initialization)
   const getPeriodLabelByKey = (key) => {
      const periodsMap = {
         today: 'Today',
         yesterday: 'Yesterday',
         thisWeek: 'This Week',
         lastWeek: 'Last Week',
         last7Days: 'Last 7 Days',
         last30Days: 'Last 30 Days',
         thisMonth: 'This Month',
         lastMonth: 'Last Month',
         thisQuarter: 'This Quarter',
         lastQuarter: 'Last Quarter',
         thisFinancialYear: 'This Financial Year',
         lastFinancialYear: 'Last Financial Year',
         customRange: 'Select Date Range',
      };
      return periodsMap[key] || 'This Financial Year';
   };

   const [selectedPeriod, setSelectedPeriod] = useState(getPeriodLabelByKey(defaultPeriod));
   const [isCustomRange, setIsCustomRange] = useState(false);
   const [isInitialized, setIsInitialized] = useState(false);
   const { isOpen, onOpen, onClose } = useDisclosure();
   const litepickerRef = useRef(null);
   const pickerInstance = useRef(null);

   // Color mode values
   const buttonBg = useColorModeValue('white', 'gray.800');
   const buttonBorder = useColorModeValue('gray.200', 'gray.600');
   const buttonBorderHover = useColorModeValue('gray.300', 'gray.500');
   const buttonColor = useColorModeValue('gray.800', 'whiteAlpha.900');
   const buttonColorPlaceholder = useColorModeValue('gray.400', 'gray.400');
   const buttonFocusBorder = useColorModeValue('blue.500', 'blue.300');
   const buttonFocusBoxShadow = useColorModeValue('0 0 0 1px #3182ce', '0 0 0 1px #63B3ED');

   const popoverBg = useColorModeValue('white', 'gray.800');
   const popoverBorder = useColorModeValue('gray.200', 'gray.600');
   const popoverBoxShadow = useColorModeValue('md', 'dark-lg');

   const itemBg = useColorModeValue('transparent', 'transparent');
   const itemBgHover = useColorModeValue('gray.50', 'gray.700');
   const itemBgSelected = useColorModeValue('blue.50', 'blue.900');
   const itemColor = useColorModeValue('gray.800', 'whiteAlpha.900');
   const itemColorSelected = useColorModeValue('blue.600', 'blue.200');
   const checkIconColor = useColorModeValue('blue.500', 'blue.300');

   const inputBg = useColorModeValue('white', 'gray.800');
   const inputBorder = useColorModeValue('gray.200', 'gray.600');
   const inputBorderHover = useColorModeValue('gray.300', 'gray.500');
   const inputFocusBorder = useColorModeValue('blue.500', 'blue.300');
   const inputFocusBoxShadow = useColorModeValue('0 0 0 1px #3182ce', '0 0 0 1px #63B3ED');
   const inputColor = useColorModeValue('gray.800', 'whiteAlpha.900');
   const inputPlaceholderColor = useColorModeValue('gray.400', 'gray.400');

   const dividerColor = useColorModeValue('gray.200', 'gray.600');

   // Define predefined periods
   const periods = [
      { key: 'today', label: 'Today' },
      { key: 'yesterday', label: 'Yesterday' },
      { key: 'thisWeek', label: 'This Week' },
      { key: 'lastWeek', label: 'Last Week' },
      { key: 'last7Days', label: 'Last 7 Days' },
      { key: 'last30Days', label: 'Last 30 Days' },
      { key: 'thisMonth', label: 'This Month' },
      { key: 'lastMonth', label: 'Last Month' },
      { key: 'thisQuarter', label: 'This Quarter' },
      { key: 'lastQuarter', label: 'Last Quarter' },
      { key: 'thisFinancialYear', label: 'This Financial Year' },
      { key: 'lastFinancialYear', label: 'Last Financial Year' },
      { key: 'customRange', label: 'Select Date Range' },
   ];

   // Helper function to ensure we always return moment objects
   const ensureMoment = (date) => {
      return moment.isMoment(date) ? date : moment(date);
   };

   // Calculate date ranges for each period
   const getDateRange = (periodKey) => {
      const date = moment().utcOffset(0, true);

      switch (periodKey) {
         case 'today':
            return [moment().utcOffset(0, true).startOf('day'), moment().utcOffset(0, true).endOf('day')];
         case 'yesterday':
            return [date.clone().subtract(1, 'day').startOf('day'), date.clone().subtract(1, 'day').endOf('day')];
         case 'thisWeek':
            return [date.clone().startOf('week'), date.clone().endOf('week')];
         case 'lastWeek':
            return [date.clone().subtract(1, 'week').startOf('week'), date.clone().subtract(1, 'week').endOf('week')];
         case 'last7Days':
            return [date.clone().subtract(6, 'days').startOf('day'), date.clone().endOf('day')];
         case 'last30Days':
            return [date.clone().subtract(29, 'days').startOf('day'), date.clone().endOf('day')];
         case 'thisMonth':
            return [date.clone().startOf('month'), date.clone().endOf('month')];
         case 'lastMonth':
            return [
               date.clone().subtract(1, 'month').startOf('month'),
               date.clone().subtract(1, 'month').endOf('month'),
            ];
         case 'thisQuarter':
            return [date.clone().startOf('quarter'), date.clone().endOf('quarter')];
         case 'lastQuarter':
            return [
               date.clone().subtract(1, 'quarter').startOf('quarter'),
               date.clone().subtract(1, 'quarter').endOf('quarter'),
            ];
         case 'thisFinancialYear':
            const currentYear = date.year();
            const financialYearStart =
               date.month() >= 3
                  ? moment([currentYear, 3, 1]).utcOffset(0, true)
                  : moment([currentYear - 1, 3, 1]).utcOffset(0, true);
            const financialYearEnd = financialYearStart.clone().add(1, 'year').subtract(1, 'day').endOf('day');
            return [financialYearStart, financialYearEnd];
         case 'lastFinancialYear':
            const lastYear = date.year();
            const lastFinancialYearStart =
               date.month() >= 3
                  ? moment([lastYear - 1, 3, 1]).utcOffset(0, true)
                  : moment([lastYear - 2, 3, 1]).utcOffset(0, true);
            const lastFinancialYearEnd = lastFinancialYearStart.clone().add(1, 'year').subtract(1, 'day').endOf('day');
            return [lastFinancialYearStart, lastFinancialYearEnd];
         default:
            return [date.clone().startOf('day'), date.clone().endOf('day')];
      }
   };

   // Enhanced dark mode styles for Litepicker
   useEffect(() => {
      const createDarkModeStyles = () => {
         const existingStyle = document.getElementById('litepicker-dark-mode-styles');
         if (existingStyle) {
            existingStyle.remove();
         }

         const style = document.createElement('style');
         style.id = 'litepicker-dark-mode-styles';

         // Get current Chakra UI color values
         const darkColors = {
            gray800: colorMode === 'dark' ? '#1A202C' : '#2D3748',
            gray700: colorMode === 'dark' ? '#2D3748' : '#4A5568',
            gray600: colorMode === 'dark' ? '#4A5568' : '#718096',
            gray400: colorMode === 'dark' ? '#A0AEC0' : '#CBD5E0',
            whiteAlpha900: colorMode === 'dark' ? 'rgba(255, 255, 255, 0.92)' : '#000000',
            blue300: colorMode === 'dark' ? '#90CDF4' : '#3182CE',
            blue500: colorMode === 'dark' ? '#3182CE' : '#3182CE',
            blue800: colorMode === 'dark' ? '#2C5282' : '#2A4365',
         };

         style.textContent = `
        /* Dark mode styles for Litepicker */
        .litepicker.dark,
        .litepicker[data-theme="dark"] {
          background-color: ${darkColors.gray800} !important;
          border: 1px solid ${darkColors.gray600} !important;
          color: ${darkColors.whiteAlpha900} !important;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.1) !important;
        }
        
        .litepicker.dark .container__main,
        .litepicker[data-theme="dark"] .container__main {
          background-color: ${darkColors.gray800} !important;
          border: none !important;
        }
        
        .litepicker.dark .container__days,
        .litepicker[data-theme="dark"] .container__days {
          background-color: ${darkColors.gray800} !important;
        }
        
        .litepicker.dark .container__months,
        .litepicker[data-theme="dark"] .container__months {
          background-color: ${darkColors.gray800} !important;
        }
        
        .litepicker.dark .container__years,
        .litepicker[data-theme="dark"] .container__years {
          background-color: ${darkColors.gray800} !important;
        }
        
        .litepicker.dark .month-item-name,
        .litepicker.dark .month-item-year,
        .litepicker[data-theme="dark"] .month-item-name,
        .litepicker[data-theme="dark"] .month-item-year {
          color: ${darkColors.whiteAlpha900} !important;
        }
        
        .litepicker.dark .month-item-header > div,
        .litepicker[data-theme="dark"] .month-item-header > div {
          color: ${darkColors.whiteAlpha900} !important;
        }
        
        .litepicker.dark .day-item,
        .litepicker[data-theme="dark"] .day-item {
          color: ${darkColors.whiteAlpha900} !important;
          background-color: transparent !important;
        }
        
        .litepicker.dark .day-item:hover,
        .litepicker[data-theme="dark"] .day-item:hover {
          background-color: ${darkColors.gray700} !important;
          color: ${darkColors.whiteAlpha900} !important;
        }
        
        .litepicker.dark .day-item.is-today,
        .litepicker[data-theme="dark"] .day-item.is-today {
          color: ${darkColors.blue300} !important;
          background-color: transparent !important;
        }
        
        .litepicker.dark .day-item.is-selected,
        .litepicker[data-theme="dark"] .day-item.is-selected {
          background-color: ${darkColors.blue500} !important;
          color: white !important;
        }
        
        .litepicker.dark .day-item.is-start-date,
        .litepicker.dark .day-item.is-end-date,
        .litepicker[data-theme="dark"] .day-item.is-start-date,
        .litepicker[data-theme="dark"] .day-item.is-end-date {
          background-color: ${darkColors.blue500} !important;
          color: white !important;
        }
        
        .litepicker.dark .day-item.is-in-range,
        .litepicker[data-theme="dark"] .day-item.is-in-range {
          background-color: ${darkColors.blue800} !important;
          color: white !important;
        }
        
        .litepicker.dark .button-next-month,
        .litepicker.dark .button-prev-month,
        .litepicker[data-theme="dark"] .button-next-month,
        .litepicker[data-theme="dark"] .button-prev-month {
          color: ${darkColors.whiteAlpha900} !important;
          background-color: transparent !important;
        }
        
        .litepicker.dark .button-next-month:hover,
        .litepicker.dark .button-prev-month:hover,
        .litepicker[data-theme="dark"] .button-next-month:hover,
        .litepicker[data-theme="dark"] .button-prev-month:hover {
          color: ${darkColors.blue300} !important;
          background-color: ${darkColors.gray700} !important;
        }
        
        .litepicker.dark .week-item,
        .litepicker[data-theme="dark"] .week-item {
          color: ${darkColors.gray400} !important;
        }
        
        .litepicker.dark .month-item,
        .litepicker[data-theme="dark"] .month-item {
          color: ${darkColors.whiteAlpha900} !important;
        }
        
        .litepicker.dark .month-item:hover,
        .litepicker[data-theme="dark"] .month-item:hover {
          background-color: ${darkColors.gray700} !important;
          color: ${darkColors.whiteAlpha900} !important;
        }
        
        .litepicker.dark .month-item.is-selected,
        .litepicker[data-theme="dark"] .month-item.is-selected {
          background-color: ${darkColors.blue500} !important;
          color: white !important;
        }
        
        .litepicker.dark .year-item,
        .litepicker[data-theme="dark"] .year-item {
          color: ${darkColors.whiteAlpha900} !important;
        }
        
        .litepicker.dark .year-item:hover,
        .litepicker[data-theme="dark"] .year-item:hover {
          background-color: ${darkColors.gray700} !important;
          color: ${darkColors.whiteAlpha900} !important;
        }
        
        .litepicker.dark .year-item.is-selected,
        .litepicker[data-theme="dark"] .year-item.is-selected {
          background-color: ${darkColors.blue500} !important;
          color: white !important;
        }
        
        /* Additional dark mode fixes */
        .litepicker.dark .container__tooltip,
        .litepicker[data-theme="dark"] .container__tooltip {
          background-color: ${darkColors.gray700} !important;
          color: ${darkColors.whiteAlpha900} !important;
          border: 1px solid ${darkColors.gray600} !important;
        }
        
        .litepicker.dark .container__footer,
        .litepicker[data-theme="dark"] .container__footer {
          background-color: ${darkColors.gray800} !important;
          border-top: 1px solid ${darkColors.gray600} !important;
        }
        
        /* Dropdown styling */
        .litepicker.dark .container__dropdown,
        .litepicker[data-theme="dark"] .container__dropdown {
          background-color: ${darkColors.gray800} !important;
          border: 1px solid ${darkColors.gray600} !important;
        }
        
        .litepicker.dark .container__dropdown .dropdown-item,
        .litepicker[data-theme="dark"] .container__dropdown .dropdown-item {
          color: ${darkColors.whiteAlpha900} !important;
        }
        
        .litepicker.dark .container__dropdown .dropdown-item:hover,
        .litepicker[data-theme="dark"] .container__dropdown .dropdown-item:hover {
          background-color: ${darkColors.gray700} !important;
        }
      `;

         document.head.appendChild(style);
      };

      createDarkModeStyles();

      // Cleanup function
      return () => {
         const existingStyle = document.getElementById('litepicker-dark-mode-styles');
         if (existingStyle) {
            existingStyle.remove();
         }
      };
   }, [colorMode]); // Re-run when color mode changes

   // Initialize Litepicker for custom range
   useEffect(() => {
      if (!isCustomRange || !litepickerRef.current) return;

      const timer = setTimeout(() => {
         if (!litepickerRef.current) return;

         if (pickerInstance.current) {
            pickerInstance.current.destroy();
            pickerInstance.current = null;
         }

         pickerInstance.current = new Litepicker({
            element: litepickerRef.current,
            singleMode: false,
            format: 'DD/MMM/YYYY',
            delimiter: ' - ',
            numberOfColumns: 2,
            numberOfMonths: 2,
            splitView: true,
            autoApply: true,

            setup: (picker) => {
               picker.on('selected', (date1, date2) => {
                  const range = [
                     moment(date1.dateInstance).startOf('day'),
                     moment(date2.dateInstance).endOf('day'),
                  ];

                  onChange?.(range);
                  setSelectedPeriod('Custom Range');
                  setIsCustomRange(false);
                  onClose();
               });
            },
         });

         requestAnimationFrame(() => {
            pickerInstance.current?.show();
         });

      }, 50);

      return () => {
         clearTimeout(timer);
         if (pickerInstance.current) {
            pickerInstance.current.destroy();
            pickerInstance.current = null;
         }
      };
   }, [isCustomRange]);

   const handlePeriodSelect = (periodKey, event) => {
      if (periodKey === 'customRange') {
         event.stopPropagation();
         setIsCustomRange(true);
         setSelectedPeriod('Select Date Range');
         return;
      }

      const dateRange = getDateRange(periodKey);
      const period = periods.find((p) => p.key === periodKey);
      const safeDateRange = [ensureMoment(dateRange[0]).startOf('day'), ensureMoment(dateRange[1]).endOf('day')];

      setSelectedPeriod(period.label);
      setIsCustomRange(false);
      onChange && onChange(safeDateRange);
      onClose();
   };

   // Initialize with default range if no value is provided or set from defaultPeriod
   useEffect(() => {
      if (!isInitialized) {
         // Always set the default period on first load
         const initialRange = getDateRange(defaultPeriod);
         const initialLabel = getPeriodLabelByKey(defaultPeriod);
         setSelectedPeriod(initialLabel);
         onChange && onChange(initialRange);
         setIsInitialized(true);
      }
   }, [defaultPeriod, isInitialized, onChange]); // Include all dependencies

   return (
      <Box width="100%" maxW="300px" {...props} zIndex={'20'}>
         <Popover
            isOpen={isOpen}
            onOpen={onOpen}
            onClose={onClose}
            placement="bottom-start"
            closeOnBlur={!isCustomRange}>
            <PopoverTrigger>
               <Button
                  variant="outline"
                  width="100%"
                  height="32px"
                  justifyContent="space-between"
                  rightIcon={<ChevronDownIcon />}
                  fontSize="14px"
                  fontWeight="normal"
                  borderColor={buttonBorder}
                  bg={buttonBg}
                  color={selectedPeriod === 'Select Date Range' ? buttonColorPlaceholder : buttonColor}
                  _hover={{
                     borderColor: buttonBorderHover,
                  }}
                  _focus={{
                     borderColor: buttonFocusBorder,
                     boxShadow: buttonFocusBoxShadow,
                  }}
                  style={style}>
                  <Text as="span" textAlign="left" flex="1">
                     {selectedPeriod}
                  </Text>
               </Button>
            </PopoverTrigger>

            <PopoverContent
               width="100%"
               minW="300px"
               border="1px solid"
               borderColor={popoverBorder}
               borderRadius="6px"
               boxShadow={popoverBoxShadow}
               bg={popoverBg}
               zIndex={1050}>
               <PopoverBody p="0">
                  <VStack spacing="0" align="stretch">
                     {periods.map((period) => (
                        <Box
                           key={period.key}
                           px="16px"
                           py="8px"
                           cursor="pointer"
                           bg={selectedPeriod === period.label ? itemBgSelected : itemBg}
                           color={selectedPeriod === period.label ? itemColorSelected : itemColor}
                           transition="all 0.2s"
                           _hover={{
                              bg: selectedPeriod !== period.label ? itemBgHover : itemBgSelected,
                           }}
                           onClick={(event) => handlePeriodSelect(period.key, event)}
                           display="flex"
                           justifyContent="space-between"
                           alignItems="center">
                           <Text fontSize="14px">{period.label}</Text>
                           {selectedPeriod === period.label && <CheckIcon w="12px" h="12px" color={checkIconColor} />}
                        </Box>
                     ))}

                     {isCustomRange && (
                        <Box>
                           <Divider borderColor={dividerColor} />
                           <Box p="12px">
                              <Input
                                 ref={litepickerRef}
                                 placeholder="Select custom date range"
                                 readOnly
                                 height="32px"
                                 fontSize="14px"
                                 cursor="pointer"
                                 bg={inputBg}
                                 borderColor={inputBorder}
                                 color={inputColor}
                                 _placeholder={{
                                    color: inputPlaceholderColor,
                                 }}
                                 _hover={{
                                    borderColor: inputBorderHover,
                                 }}
                                 _focus={{
                                    borderColor: inputFocusBorder,
                                    boxShadow: inputFocusBoxShadow,
                                 }}
                              />
                           </Box>
                        </Box>
                     )}
                  </VStack>
               </PopoverBody>
            </PopoverContent>
         </Popover>
      </Box>
   );
};

export default PeriodDatePicker;
